/**
 * <ImageUploader />
 *
 * Multi-image uploader that POSTs to /api/upload (server writes to local
 * filesystem; no S3).  Returns the server-side public URLs to the parent
 * via `onChange`.
 *
 * Behaviour:
 *   * Accepts jpeg / png / webp / gif / avif.
 *   * Uploads as soon as the user picks files (shows inline spinner).
 *   * Displays previews with remove buttons.
 *   * Stays controlled -- the parent owns the `value` list of URLs.
 */
import * as React from "react";
import axios from "axios";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { API_BASE } from "../../lib/api";
import { cn } from "../../lib/cn";

interface Props {
  /** Current list of uploaded URLs (controlled). */
  value: string[];
  /** Parent handler for the updated list. */
  onChange: (urls: string[]) => void;
  /** Max files allowed. Default 5. */
  max?: number;
  /** Optional test ID prefix. */
  testid?: string;
}

/**
 * Turn a server-relative URL like `/api/uploads/...` into a full browser
 * URL that resolves via the same ingress as `REACT_APP_BACKEND_URL`.
 */
export function resolveUploadUrl(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL ?? "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function ImageUploader({ value, onChange, max = 5, testid = "image-uploader" }: Props) {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const pick = () => inputRef.current?.click();

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setError(null);

    const availableSlots = Math.max(0, max - value.length);
    const files = picked.slice(0, availableSlots);
    if (files.length === 0) {
      setError(`You can upload at most ${max} images.`);
      e.target.value = "";
      return;
    }

    const form = new FormData();
    files.forEach((f) => form.append("files", f));

    setUploading(true);
    try {
      // Use axios directly so we can send multipart. Credentials on so the
      // auth cookie is included.
      const r = await axios.post<{ urls: string[]; count: number }>(
        `${API_BASE}/upload`,
        form,
        { withCredentials: true },
      );
      onChange([...value, ...r.data.urls]);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      // Reset so the same file can be picked twice.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const canAddMore = value.length < max;

  return (
    <div data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,image/gif,image/avif,video/mp4,video/webm,video/quicktime"
        onChange={handleFiles}
        className="sr-only"
        data-testid={`${testid}-input`}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((url, i) => {
          const resolved = resolveUploadUrl(url);
          const isVideo = /\.(mp4|webm|mov|ogv)$/i.test(url);
          return (
            <div
              key={url + i}
              className="relative aspect-square rounded-xl overflow-hidden border-2 border-surface-border bg-surface-sunken"
              data-testid={`${testid}-thumb-${i}`}
            >
              {isVideo ? (
                <video
                  src={resolved}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover"
                />
              ) : (
                <img src={resolved} alt="" className="h-full w-full object-cover" />
              )}
              {isVideo && (
                <span className="absolute bottom-1 left-1 rounded bg-ink/80 text-white text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-wider">
                  Video
                </span>
              )}
              <button
                type="button"
                aria-label="Remove media"
                onClick={() => removeAt(i)}
                data-testid={`${testid}-remove-${i}`}
                className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white/95 text-brand border border-surface-border shadow-sm hover:bg-brand hover:text-white transition-colors"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          );
        })}

        {canAddMore && (
          <button
            type="button"
            onClick={pick}
            disabled={uploading}
            data-testid={`${testid}-trigger`}
            className={cn(
              "aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2",
              uploading
                ? "border-ink-muted bg-surface-sunken text-ink-muted"
                : "border-surface-border bg-white text-ink-muted hover:border-ink hover:text-ink",
            )}
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            ) : (
              <>
                <ImagePlus className="h-6 w-6" aria-hidden />
                <span className="text-xs font-semibold">Add photos</span>
              </>
            )}
          </button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          data-testid={`${testid}-error`}
          className="mt-2 text-sm font-medium text-brand"
        >
          {error}
        </p>
      )}
      <p className="mt-2 text-xs text-ink-muted">
        PNG, JPG, WEBP, GIF or MP4/WEBM/MOV video. Max {max} items · 100 MB each.
      </p>
    </div>
  );
}
